import {
  CHUNK_SIZE,
  type ChunkData,
  chunkKeyInt,
  getTileIndex,
  type TileTypeValue,
  type WorldObjectSnapshot,
} from '@dreamgame/shared';
import { signal } from '@preact/signals';

// Extended chunk data with objects
export interface ChunkWithObjects extends ChunkData {
  readonly objects: ReadonlyArray<WorldObjectSnapshot>;
}

// Chunk storage keyed by integer key for O(1) lookup
const chunksMap = signal<Map<number, ChunkWithObjects>>(new Map());

// Pending requests with timestamps for timeout handling
interface PendingRequest {
  readonly timestamp: number;
}
const pendingRequests = signal<Map<number, PendingRequest>>(new Map());

// Timeout for pending chunk requests (5 seconds)
const PENDING_CHUNK_TIMEOUT_MS = 5000;

// Get a loaded chunk
export const getChunk = ({ x, y }: { x: number; y: number }): ChunkWithObjects | undefined => {
  return chunksMap.value.get(chunkKeyInt({ x, y }));
};

// Check if a chunk is loaded
export const hasChunk = ({ x, y }: { x: number; y: number }): boolean => {
  return chunksMap.value.has(chunkKeyInt({ x, y }));
};

// Check if a chunk request is pending (and not timed out)
export const isChunkPending = ({ x, y }: { x: number; y: number }): boolean => {
  const key = chunkKeyInt({ x, y });
  const pending = pendingRequests.value.get(key);
  if (!pending) {
    return false;
  }

  // Check if request has timed out
  const now = Date.now();
  if (now - pending.timestamp > PENDING_CHUNK_TIMEOUT_MS) {
    // Remove timed out request so it can be re-requested
    const newPending = new Map(pendingRequests.value);
    newPending.delete(key);
    pendingRequests.value = newPending;
    return false;
  }

  return true;
};

// Mark a chunk as being requested
export const markChunkPending = ({ x, y }: { x: number; y: number }): void => {
  const key = chunkKeyInt({ x, y });
  const newPending = new Map(pendingRequests.value);
  newPending.set(key, { timestamp: Date.now() });
  pendingRequests.value = newPending;
};

// Store a received chunk with objects
export const setChunk = ({
  chunk,
  objects = [],
}: {
  chunk: ChunkData;
  objects?: ReadonlyArray<WorldObjectSnapshot>;
}): void => {
  const key = chunkKeyInt({ x: chunk.x, y: chunk.y });

  // Remove from pending
  const newPending = new Map(pendingRequests.value);
  newPending.delete(key);
  pendingRequests.value = newPending;

  // Add to loaded chunks with objects
  const newChunks = new Map(chunksMap.value);
  const chunkWithObjects: ChunkWithObjects = {
    ...chunk,
    objects,
  };
  newChunks.set(key, chunkWithObjects);
  chunksMap.value = newChunks;
};

// Get tile type at world tile coordinates
export const getTileAtWorld = ({
  tileX,
  tileY,
}: {
  tileX: number;
  tileY: number;
}): TileTypeValue | undefined => {
  const chunkX = Math.floor(tileX / CHUNK_SIZE);
  const chunkY = Math.floor(tileY / CHUNK_SIZE);
  const localX = ((tileX % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;
  const localY = ((tileY % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE;

  const chunk = getChunk({ x: chunkX, y: chunkY });
  if (!chunk) {
    return undefined;
  }

  const index = getTileIndex({ localX, localY, chunkSize: CHUNK_SIZE });
  return chunk.tiles[index] as TileTypeValue;
};

// Get all loaded chunks (for rendering)
export const getAllChunks = (): ReadonlyMap<number, ChunkWithObjects> => {
  return chunksMap.value;
};

// Get objects in a specific chunk
export const getChunkObjects = ({
  x,
  y,
}: {
  x: number;
  y: number;
}): ReadonlyArray<WorldObjectSnapshot> => {
  const chunk = getChunk({ x, y });
  return chunk?.objects ?? [];
};

// Get all objects from all loaded chunks
export const getAllObjects = (): ReadonlyArray<WorldObjectSnapshot> => {
  const allObjects: WorldObjectSnapshot[] = [];
  for (const chunk of chunksMap.value.values()) {
    allObjects.push(...chunk.objects);
  }
  return allObjects;
};

// Clear all chunks (on disconnect)
export const clearChunks = (): void => {
  chunksMap.value = new Map();
  pendingRequests.value = new Map();
};

// Drop chunks outside a square radius around a center chunk.
// Useful for lightweight viewers/tools so memory usage stays bounded.
export const pruneChunksByRadius = ({
  centerChunkX,
  centerChunkY,
  radius,
}: {
  centerChunkX: number;
  centerChunkY: number;
  radius: number;
}): void => {
  const retainedChunks = new Map<number, ChunkWithObjects>();
  for (const [key, chunk] of chunksMap.value) {
    const dx = Math.abs(chunk.x - centerChunkX);
    const dy = Math.abs(chunk.y - centerChunkY);
    if (dx <= radius && dy <= radius) {
      retainedChunks.set(key, chunk);
    }
  }
  if (retainedChunks.size !== chunksMap.value.size) {
    chunksMap.value = retainedChunks;
  }

  const retainedPending = new Map<number, PendingRequest>();
  for (const [key, request] of pendingRequests.value) {
    const chunk = chunksMap.value.get(key);
    if (chunk) {
      retainedPending.set(key, request);
    }
  }
  if (retainedPending.size !== pendingRequests.value.size) {
    pendingRequests.value = retainedPending;
  }
};

// Get chunks that need to be requested for visible area
export const getChunksToRequest = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<{ x: number; y: number }>;
}): Array<{ x: number; y: number }> => {
  return visibleChunks.filter(({ x, y }) => !hasChunk({ x, y }) && !isChunkPending({ x, y }));
};

// Add a world object to a chunk (used when construction completes)
export const addObjectToChunk = ({ object }: { object: WorldObjectSnapshot }): void => {
  const chunkX = Math.floor(object.tileX / CHUNK_SIZE);
  const chunkY = Math.floor(object.tileY / CHUNK_SIZE);
  const key = chunkKeyInt({ x: chunkX, y: chunkY });

  const existingChunk = chunksMap.value.get(key);
  if (!existingChunk) {
    // Chunk not loaded, object will be included when chunk is requested
    return;
  }

  // Add object to chunk
  const newChunks = new Map(chunksMap.value);
  const updatedChunk: ChunkWithObjects = {
    ...existingChunk,
    objects: [...existingChunk.objects, object],
  };
  newChunks.set(key, updatedChunk);
  chunksMap.value = newChunks;
};

// Remove a world object from a chunk (used when object is destroyed/harvested)
export const removeObjectFromChunk = ({
  networkId,
  tileX,
  tileY,
}: {
  networkId: number;
  tileX: number;
  tileY: number;
}): void => {
  const chunkX = Math.floor(tileX / CHUNK_SIZE);
  const chunkY = Math.floor(tileY / CHUNK_SIZE);
  const key = chunkKeyInt({ x: chunkX, y: chunkY });

  const existingChunk = chunksMap.value.get(key);
  if (!existingChunk) {
    return;
  }

  const filteredObjects = existingChunk.objects.filter((obj) => obj.networkId !== networkId);
  if (filteredObjects.length === existingChunk.objects.length) {
    return; // Object not found in this chunk
  }

  const newChunks = new Map(chunksMap.value);
  const updatedChunk: ChunkWithObjects = {
    ...existingChunk,
    objects: filteredObjects,
  };
  newChunks.set(key, updatedChunk);
  chunksMap.value = newChunks;
};

// Update a world object in its chunk (used for state/rotation updates)
export const updateObjectInChunk = ({ object }: { object: WorldObjectSnapshot }): void => {
  const chunkX = Math.floor(object.tileX / CHUNK_SIZE);
  const chunkY = Math.floor(object.tileY / CHUNK_SIZE);
  const key = chunkKeyInt({ x: chunkX, y: chunkY });
  const existingChunk = chunksMap.value.get(key);
  if (!existingChunk) {
    return;
  }

  const objectIndex = existingChunk.objects.findIndex((obj) => obj.networkId === object.networkId);
  if (objectIndex < 0) {
    return;
  }

  const existingObject = existingChunk.objects[objectIndex];
  if (
    existingObject &&
    existingObject.rotation === object.rotation &&
    existingObject.state === object.state &&
    existingObject.ownerId === object.ownerId
  ) {
    return;
  }

  const updatedObjects = [...existingChunk.objects];
  updatedObjects[objectIndex] = object;
  const newChunks = new Map(chunksMap.value);
  newChunks.set(key, { ...existingChunk, objects: updatedObjects });
  chunksMap.value = newChunks;
};
